import React, {
  Dispatch,
  FC,
  Fragment,
  useMemo,
  useState,
  RefObject,
  MutableRefObject,
  useCallback
} from 'react';
import { Box, Checkbox, TableBody, TableCell, TableRow, Collapse, SxProps } from '@mui/material';
import { classes } from './styles';
import { handleSelectOne, handleScrollableEnter, handleScrollableExit } from './tableHelper';
import {
  GenericTableData,
  TSortableColumn,
  TTableState,
  TableActionItemAction,
  TableSelectionAction,
  TableScrollAction
} from './types';
import GenericTableActions, { ActionListItem } from './GenericTableActions';

interface IGenericTableBodyProps<T> {
  tableState: TTableState<T, TSortableColumn[]>;
  dispatch: Dispatch<TableActionItemAction<T> | TableSelectionAction<T> | TableScrollAction>;
  items: T[];
  TableRows: FC<{
    column: TSortableColumn;
    item: T;
    tableHovered: boolean;
    dispatch?: Dispatch<TableActionItemAction<T> | TableSelectionAction<T>>;
    onExpand?: () => void;
  }>;
  TableActionList?: ActionListItem[];
  allowMultipleSelection?: boolean;
  ExpandedContent?: FC<{ item: T }>;
  expandableCellIds?: string[];
  customRowStyles?: Record<string, SxProps>;
  tableRef: RefObject<HTMLDivElement>;
  titleCellRef: RefObject<HTMLTableCellElement>;
  actionCellRef: RefObject<HTMLTableCellElement>;
  hoverTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  isHoveringRef: MutableRefObject<boolean>;
}

const GenericTableBody = <T extends GenericTableData>({
  tableState,
  dispatch,
  items,
  TableRows,
  TableActionList,
  allowMultipleSelection = true,
  ExpandedContent,
  expandableCellIds = [],
  customRowStyles,
  tableRef,
  titleCellRef,
  actionCellRef,
  hoverTimeoutRef,
  isHoveringRef
}: IGenericTableBodyProps<T>) => {
  const allItemIds = useMemo(() => items.map((item) => item.id), [items]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleRowExpand = (itemId: number) => {
    setExpandedRows((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleRowMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (!titleCellRef.current || !actionCellRef.current) return;

      const titleRect = titleCellRef.current.getBoundingClientRect();
      const actionRect = actionCellRef.current.getBoundingClientRect();
      const mouseX = event.clientX;

      if (mouseX > titleRect.right && mouseX < actionRect.left) {
        clearHoverTimeout();
        isHoveringRef.current = true;
        handleScrollableEnter(tableRef, dispatch);
      } else {
        isHoveringRef.current = false;
        handleScrollableExit(dispatch);
      }
    },
    [tableRef, dispatch, titleCellRef, actionCellRef, clearHoverTimeout]
  );

  const handleRowMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (
        relatedTarget &&
        (relatedTarget.id === 'left-arrow' ||
          relatedTarget.id === 'right-arrow' ||
          relatedTarget.closest('#left-arrow') ||
          relatedTarget.closest('#right-arrow'))
      ) {
        return;
      }
      isHoveringRef.current = false;
      clearHoverTimeout();
      hoverTimeoutRef.current = setTimeout(() => {
        if (!isHoveringRef.current) {
          handleScrollableExit(dispatch);
        }
        hoverTimeoutRef.current = null;
      }, 400);
    },
    [dispatch, clearHoverTimeout, hoverTimeoutRef, isHoveringRef]
  );

  return (
    <TableBody>
      {items.map((item) => (
        <Fragment key={item.id}>
          <TableRow
            sx={{ ...(ExpandedContent && { borderBottom: 'none !important' }), ...customRowStyles }}
            onMouseEnter={handleRowMouseEnter}
            onMouseLeave={handleRowMouseLeave}
          >
            {allowMultipleSelection && (
              <TableCell sx={{ ...classes.sticky, width: '58px' }}>
                <Checkbox
                  id={`select-checkbox-${item.id}`}
                  sx={classes.checkbox}
                  checked={tableState.selectedItemIds.includes(item.id)}
                  onChange={(event) => handleSelectOne(event, item, allItemIds, dispatch)}
                  inputProps={{
                    'aria-label': `select ${item.name}`
                  }}
                />
              </TableCell>
            )}
            {tableState.columns.map(
              (column) =>
                column.enabled && (
                  <TableRows
                    key={column.id}
                    item={item}
                    column={column}
                    tableHovered={tableState.tableHovered}
                    dispatch={dispatch}
                    onExpand={
                      expandableCellIds.includes(column.id)
                        ? () => handleRowExpand(item.id)
                        : undefined
                    }
                  />
                )
            )}
            <TableCell
              sx={{
                zIndex: '4',
                ...classes.actionsTableCell,
                ...classes.stickyRight,
                ...(tableState.tableHovered && classes.scrollShadowVisible),
                ...(!TableActionList && { minWidth: '24px', width: '24px' })
              }}
            >
              <Box sx={classes.actionItems}>
                {TableActionList && (
                  <GenericTableActions item={item} dispatch={dispatch} actions={TableActionList} />
                )}
              </Box>
            </TableCell>
          </TableRow>
          {ExpandedContent && (
            <TableRow>
              <TableCell
                style={{ paddingBottom: 0, paddingTop: 0 }}
                colSpan={tableState.columns.length + (allowMultipleSelection ? 2 : 1)}
              >
                <Collapse in={expandedRows.includes(item.id)} timeout="auto" unmountOnExit>
                  <Box margin={1}>
                    <ExpandedContent item={item} />
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      ))}
    </TableBody>
  );
};

export default GenericTableBody;
